/**
 * RunMap — StS-style branching progression map for a run.
 *
 * A run is a 2-act journey: each act is 6 rows deep, branching between
 * acts. Row 1 offers 2–3 entry nodes; the final row of act 2 is a single
 * boss node where all paths converge. The player picks one adjacent node
 * per step and cannot backtrack.
 *
 * Node types in Ship 1:
 *   - 'hit'   Standard kill round. Pre-assigned city, passive reward on win.
 *   - 'hq'    No round — opens the shop. No city assignment.
 *   - 'boss'  Act 2 final node, unique kill round (mechanics TBD in later ship).
 *
 * Reserved for later ships (typed now, treated as 'hit' at runtime):
 *   - 'elite'        Higher-tier agent loadout + marks bump.
 *   - 'extraction'   Kill doesn't end round; must reach extraction point.
 */

export type RunMapNodeType = 'hit' | 'elite' | 'extraction' | 'hq' | 'boss';

export interface RunMapNode {
  id: string;
  row: number;       // 1..12 (act 1 = 1..6, act 2 = 7..12)
  col: number;       // column index within the row (0-based)
  type: RunMapNodeType;
  /** City key (matches CURATED_CITIES[].name). Null for HQ nodes. */
  cityName: string | null;
  /** IDs of nodes in row+1 this node can advance to. */
  nextNodeIds: string[];
  /**
   * Pre-rolled agent loadout — generated at map creation time so the
   * run-path UI can preview the encounter in Act 1 (Act 2 keeps it hidden
   * as "UNKNOWN" until the player reveals via safehouse intel in-round).
   * Null for HQ nodes (no combat). Stored as a loosely-typed object on
   * the node to avoid a circular import between types/runMap and
   * types/game; consumers cast to AgentLoadout.
   */
  agentLoadout?: unknown | null;
}

export interface RunMap {
  /** Version tag so migrations can invalidate legacy shapes. */
  version: number;
  nodes: RunMapNode[];
  /** IDs of the entry nodes on row 1 (the player's initial choice). */
  entryNodeIds: string[];
  /** IDs of the entry nodes on row 7 (unlocked after completing any row-6 node). */
  act2EntryNodeIds: string[];
}

/** v2: disconnected-acts layout. v3: pre-rolled agent loadouts. */
export const RUN_MAP_VERSION = 3;

/** Acts are 6 rows each, 2 acts total. */
export const ROWS_PER_ACT = 6;
export const TOTAL_ROWS = ROWS_PER_ACT * 2;

/** Which act a row belongs to (1 or 2). */
export function actForRow(row: number): 1 | 2 {
  return row <= ROWS_PER_ACT ? 1 : 2;
}
